/** Writing a comment on selected text, and the comment highlights an editor shows. */
import { useState } from "react";
import { toast } from "sonner";
import { formatTimestamp } from "@brainlog/shared";
import { errorMessage } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useNoteComments } from "./useNoteComments";
import type { CommentHandlers, CommentMark } from "./commentMarks";

/** Everything an editor needs for comments on one note: highlights, handlers and the composer. */
export function useCommentsInEditor(noteId: string) {
  const me = useMe();
  const c = useNoteComments(noteId);
  const [quote, setQuote] = useState<string | null>(null);
  const open = c.comments.filter((x) => !x.resolvedAt);
  const marks: CommentMark[] = open
    .filter((x) => x.anchorQuote)
    .map((x) => ({ id: x.id, quote: x.anchorQuote!, author: x.authorName, body: x.body, when: formatTimestamp(x.createdAt, me.data?.timezone) }));
  const handlers: { marks: CommentMark[] } & CommentHandlers = {
    marks,
    onComment: (text) => setQuote(text.slice(0, 2000)),
    onResolve: (id) => {
      void c
        .resolve(id, true)
        .then(() => toast.success("Comment resolved."))
        .catch((e) => toast.error(errorMessage(e)));
    },
  };
  const ws = c.note.data?.workspace;
  const audience = (ws?.memberCount ?? 1) > 1 ? `Visible to the ${ws?.memberCount} members of this ${ws?.kind === "notebook" ? "notebook" : "project"}.` : "Visible only to you until you share this notebook or project.";
  const composer =
    quote !== null ? (
      <CommentDialog
        quote={quote}
        audience={`${audience}${c.scopeId ? " End-to-end encrypted like the note." : ""}`}
        onClose={() => setQuote(null)}
        onPost={async (body) => {
          await c.add(body, quote);
          toast.success("Comment added.");
          setQuote(null);
        }}
      />
    ) : null;
  return { comments: c.unlocked || !c.scopeId ? handlers : undefined, composer, openCount: open.length, all: c };
}

function CommentDialog({ quote, audience, onClose, onPost }: { quote: string; audience: string; onClose(): void; onPost(body: string): Promise<void> }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await onPost(body.trim());
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Comment</DialogTitle>
          <DialogDescription>{audience}</DialogDescription>
        </DialogHeader>
        <blockquote className="max-h-32 overflow-y-auto border-l-2 pl-2 font-serif text-sm text-muted-foreground italic">“{quote}”</blockquote>
        <Textarea
          autoFocus
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          aria-label="Your comment"
          placeholder="Your comment…"
          maxLength={20_000}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!body.trim() || busy}>
            Post comment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
